// v2.2.41 — shared grouped-source helper. Lifted from the agent-assist-widget's
// local `groupSourcesByDocument` (was at `stores/widget.ts:142-185`) so VA bot
// widget + agent-assist-portal can adopt the same shape without three copies
// drifting over time.
//
// Pure data transform. Given a flat list of raw chunks from a search response,
// returns a list of grouped documents where each entry is the highest-scoring
// chunk of its document plus a `pages[]` array of every sibling chunk.

export interface GroupableSource {
  doc_id?: string | null;
  chunk_id?: string | null;
  title?: string | null;
  page?: number | string | null;
  page_number?: number | null;
  uri?: string | null;
  url?: string | null;
  [key: string]: any;
}

export interface GroupedPage {
  page: number | string;
  score: number;
  chunk_id: string;
  uri?: string | null;
}

/**
 * Group raw chunk-level sources by document.
 *
 * Each document becomes one entry with the highest-scoring chunk as the
 * representative (`...src` spread) and a `pages[]` array of every sibling
 * chunk that carried a page value.
 *
 * @param rawSources Flat list of chunks from a search response.
 * @param scoreOf    Extractor for the chunk's score field. Surfaces using
 *                   `relevance_percent` (0-100) and surfaces using `score`
 *                   (0-1) both work — the helper only uses the value for
 *                   relative comparison and sorting.
 */
export function groupSourcesByDocument<T extends GroupableSource>(
  rawSources: T[],
  scoreOf: (s: T) => number,
): Array<T & { pages: GroupedPage[] }> {
  const docMap = new Map<string, { best: T; pages: GroupedPage[] }>();
  let anonCounter = 0;

  for (const src of rawSources) {
    const key = src.doc_id || src.chunk_id || src.title || `__anon_${anonCounter++}`;

    if (!docMap.has(key)) {
      docMap.set(key, { best: { ...src }, pages: [] });
    }

    const entry = docMap.get(key)!;

    const pageValue = src.page ?? src.page_number;
    if (pageValue != null && pageValue !== '') {
      entry.pages.push({
        page: pageValue,
        score: scoreOf(src),
        chunk_id: src.chunk_id || '',
        uri: src.uri || src.url,
      });
    }

    if (scoreOf(src) > scoreOf(entry.best)) {
      entry.best = { ...src };
    }
  }

  const grouped: Array<T & { pages: GroupedPage[] }> = [];
  for (const [, entry] of docMap) {
    // v2.2.42 — dedupe pages by page value, keeping the highest-scoring chunk
    // per unique page. Without this, an .xlsx whose chunker assigns the same
    // page number to every row renders as `p.2 p.2 p.2 p.2` — all chips
    // deep-link to the same anchor, redundant for the user.
    const byPage = new Map<string, GroupedPage>();
    for (const pg of entry.pages) {
      const key = String(pg.page);
      const existing = byPage.get(key);
      if (!existing || pg.score > existing.score) {
        byPage.set(key, pg);
      }
    }
    const dedupedPages = Array.from(byPage.values()).sort((a, b) => b.score - a.score);
    grouped.push({ ...entry.best, pages: dedupedPages });
  }

  grouped.sort((a, b) => scoreOf(b as T) - scoreOf(a as T));
  return grouped;
}
